import { TSize } from "@api/apiClient";
import { TProductDimensionLimits } from "@api/apiClient/fbp";
import { formatNumber, weightKgValue } from "@utils/formatValues";

// Типы ошибок валидации товара по габаритам (FbpRestockProductValidationErrorType на бэкенде)
export const DIMENSIONS_EXCEEDED = "DimensionsExceeded";
export const DIMENSIONS_NOT_SPECIFIED = "DimensionsNotSpecified";

export const isDimensionsError = (errorType?: string): boolean =>
  errorType === DIMENSIONS_EXCEEDED || errorType === DIMENSIONS_NOT_SPECIFIED;

const sizeToCmFactor: Record<string, number> = {
  mm: 0.1,
  cm: 1,
  dm: 10,
  m: 100,
};

export function sizeCmValue(size?: TSize): number {
  if (!size) return 0;
  return (sizeToCmFactor[size.unit] ?? 1) * size.value;
}

export type TDimensionViolation = {
  parameter: "Length" | "Width" | "Height" | "SidesSum" | "WeightBrutto";
  // Стороны и сумма — в см, вес — в кг
  actualValue: number;
  limitValue: number;
  unit: "cm" | "kg";
};

/**
 * Нарушения лимитов габаритов склада — та же логика, что на бэкенде
 * (FbpMarketplaceSettingsBase.CheckProductDimensions): стороны и их сумма
 * сравниваются включительно, вес брутто — строго (сам лимит уже нарушение).
 */
export function getDimensionViolations(
  product: {
    dimensions?: { length: TSize; width: TSize; height: TSize };
    weight?: TSize;
  },
  limits: TProductDimensionLimits,
): TDimensionViolation[] {
  const lengthCm = sizeCmValue(product.dimensions?.length);
  const widthCm = sizeCmValue(product.dimensions?.width);
  const heightCm = sizeCmValue(product.dimensions?.height);
  const weightKg = weightKgValue(product.weight);

  const violations: TDimensionViolation[] = [];
  const addIfExceeds = (
    parameter: TDimensionViolation["parameter"],
    actualValue: number,
    limitValue: number,
    unit: TDimensionViolation["unit"],
    limitIsAllowed: boolean,
  ) => {
    const exceeds = limitIsAllowed
      ? actualValue > limitValue
      : actualValue >= limitValue;
    if (exceeds) violations.push({ parameter, actualValue, limitValue, unit });
  };

  addIfExceeds("Length", lengthCm, limits.maxLengthCm, "cm", true);
  addIfExceeds("Width", widthCm, limits.maxWidthCm, "cm", true);
  addIfExceeds("Height", heightCm, limits.maxHeightCm, "cm", true);
  addIfExceeds(
    "SidesSum",
    lengthCm + widthCm + heightCm,
    limits.maxSidesSumCm,
    "cm",
    true,
  );
  addIfExceeds("WeightBrutto", weightKg, limits.maxWeightBruttoKg, "kg", true);

  return violations;
}

type TTranslate = (key: string, options?: Record<string, unknown>) => string;

/**
 * Текст подсказки для товара с ошибкой габаритов (пустая строка — ошибки габаритов нет).
 * Для превышения нарушения вычисляются по габаритам товара и лимитам склада.
 * Без лимитов или габаритов — общий текст без значений.
 */
export function getDimensionsErrorHint(
  item: {
    errorType?: string;
    marketplace?: string;
    dimensions?: { length: TSize; width: TSize; height: TSize };
    weight?: TSize;
  },
  wbLimits: TProductDimensionLimits | undefined,
  aliLimits: TProductDimensionLimits | undefined,
  ymLimits: TProductDimensionLimits | undefined,
  t: TTranslate,
): string {
  if (item.errorType === DIMENSIONS_NOT_SPECIFIED) {
    return t("fbpPage.createOrder.dimensionHints.notSpecified", {
      marketplace: t(`marketplace.${item.marketplace}`, { defaultValue: item.marketplace }),
    });
  }
  if (item.errorType !== DIMENSIONS_EXCEEDED) return "";

  const limits = {
    "Wildberries": wbLimits,
    "Aliexpress": aliLimits,
    "Yandex": ymLimits,
  }[item.marketplace ?? ""];

  const violations =
    limits && item.dimensions && item.weight
      ? getDimensionViolations(item, limits)
      : [];
  if (!violations.length) {
    return t("fbpPage.createOrder.errorTypes.DimensionsExceeded", {
      marketplace: t(`marketplace.${item.marketplace}`, { defaultValue: item.marketplace }),
    });
  }
  return t("fbpPage.createOrder.dimensionHints.exceeded", {
    // Тултип рендерит строку как HTML (html-react-parser) — каждое нарушение с новой строки
    violations: violations.map((v) => t("fbpPage.createOrder.dimensionHints.violation", {
      parameter: t(`fbpPage.createOrder.dimensionHints.parameters.${v.parameter}`),
      actual: formatNumber(v.actualValue),
      limit: formatNumber(v.limitValue),
      unit: t(`fbpPage.createOrder.dimensionHints.units.${v.unit}`),
    })).join("<br/>"),
    marketplace: t(`marketplace.${item.marketplace}`, { defaultValue: item.marketplace }),
  });
}
